import { GetOrderDetailData } from "services/interface";

/** ค่าที่ override ได้จาก webhook / order list cache (ดู ModalOrderDetail) */
export interface OrderSummaryOverride {
  paid_amt?: number;
  payment_status?: string;
}

export interface OrderSummary {
  /** ผลรวมราคาสินค้าที่คำนวณเองจาก items (ใช้เป็น fallback เมื่อ backend ไม่ส่ง total_order_price) */
  totalPriceOriginal: number;
  /** ผลรวม total_price ของทุกรายการ */
  totalPrice: number;
  /** ส่วนต่างราคาปกติกับราคาสิทธิพิเศษ (เฉพาะออเดอร์ VIP) */
  totalPricePrivilege: number;
  /** ยอดรวมสินค้า (ไม่รวมค่าส่ง, ตัดรายการที่ยกเลิกออกแล้วจากฝั่ง backend) */
  totalOrderPrice: number;
  /** ค่าจัดส่ง */
  totalTransportAmount: number;
  /** ค่าสินค้า + ค่าส่ง - ส่วนลด (fallback เมื่อ backend ไม่ส่งยอดสรุปมา) */
  resultPrice: number;
  /** ยอดรวมทั้งหมด (ค่าสินค้า + ค่าขนส่ง ยังไม่หักส่วนลด) */
  totalAmountPrice: number;
  /** ยอดชำระเงินรวม */
  displayPaymentAmount: number;
  /** สถานะการชำระเงินล่าสุด (override ก่อน แล้วค่อยของ item) */
  paymentStatus: string | undefined;
  /** ยอดเงินคงเหลือที่ต้องชำระ (ติดลบ = ชำระเงินเกิน) */
  remainingAmount: number;
  showVipDiscount: boolean;
  showFactoryDiscount: boolean;
  /** ราคาสุทธิ — ยอด "รวมทั้งสิ้น" ของใบส่งของ */
  netPaidAmount: number;
  /** % vat เป็นเลขจำนวนเต็ม เช่น 7 */
  vatPercent: number;
  /** vat ที่รวมอยู่ในราคาสุทธิ */
  vatAmount: number;
  /** ราคาหลังหัก vat — ยอด "รวมเป็นเงิน" ของใบส่งของ */
  amountAfterVat: number;
}

const round2 = (value: number) => Number(value.toFixed(2));

/**
 * สรุปยอดเงินของออเดอร์ — ใช้ร่วมกันระหว่าง Modal Order Detail, ใบสั่งซื้อ (รูป) และใบส่งของ
 * เพื่อให้ทั้ง 3 จอแสดงตัวเลขชุดเดียวกันเสมอ
 *
 * ยึดฟิลด์สรุปยอดจาก backend เป็นหลัก (total_order_price / total_amount_price / paid_amt /
 * net_paid_amount) เพราะ backend ตัดรายการที่ถูกยกเลิกออกให้แล้ว และ paid_amt ถูก recompute
 * ทุกครั้งที่ webhook เข้ามา — ค่าที่คำนวณเองจาก items ใช้เป็น fallback เท่านั้น
 */
export const getOrderSummary = (
  item?: GetOrderDetailData,
  override?: OrderSummaryOverride
): OrderSummary => {
  const totalPriceOriginal =
    item?.items.reduce((acc, cur) => {
      if (item?.ref_source === "retail") {
        return acc + cur.wholesale_price * cur.sku_cnt;
      }
      return acc + cur.sku_price * cur.sku_cnt;
    }, 0) ?? 0;

  const totalPrice = item?.items.reduce((acc, cur) => acc + cur.total_price, 0) ?? 0;

  const totalPricePrivilege = item?.order_is_vip ? totalPriceOriginal - totalPrice : 0;

  const totalOrderPrice = item?.total_order_price ?? totalPriceOriginal;

  const totalTransportAmount = item?.transport_price ?? 0;

  const resultPrice =
    totalPrice + (item?.transport_price ?? 0) - (item?.discount_amount ?? 0);

  const totalAmountPrice = item?.total_amount_price ?? resultPrice;

  const displayPaymentAmount = Number(override?.paid_amt ?? item?.paid_amt ?? 0);

  const paymentStatus = override?.payment_status ?? item?.payment_status;

  const netPaidAmount =
    item?.net_paid_amount ?? item?.pre_vat_amount ?? item?.result_price ?? resultPrice;

  const vatPercent = item?.vat_percent ?? 7;

  // fallback: ถอด vat ออกจากราคาที่รวม vat แล้ว (สูตรเดิมของใบส่งของ 100/107)
  const vatAmount =
    item?.vat_amount ?? netPaidAmount - (netPaidAmount * 100) / (100 + vatPercent);

  const amountAfterVat = item?.amount_after_vat ?? netPaidAmount - vatAmount;

  return {
    totalPriceOriginal,
    totalPrice,
    totalPricePrivilege,
    totalOrderPrice,
    totalTransportAmount,
    resultPrice,
    totalAmountPrice,
    displayPaymentAmount,
    paymentStatus,
    remainingAmount: round2(netPaidAmount - displayPaymentAmount),
    showVipDiscount: !!item?.order_is_vip && (item?.extra_discount_amount ?? 0) > 0,
    showFactoryDiscount: (item?.discount_amount ?? 0) > 0,
    netPaidAmount,
    vatPercent,
    vatAmount,
    amountAfterVat,
  };
};
